import { Col, Row } from "reactstrap";
import { useCallback, useMemo } from "react";
import {
  familyInformationModal,
  personalInformationModal,
} from "../../../../../utils/Constant";
import {
  FamilyInformation,
  ModalType,
  PersonalInformation,
} from "../../../../../Types/UserProfileDetails";
import { openModal } from "../../../../../ReduxToolkit/Slice/UserProfileDetailsModalSlice";
import { useAppDispatch } from "../../../../../ReduxToolkit/Hooks";
import EmployeeInfoCard from "../../EmployeeInfoCard";
import { useEmployeeProfile } from "../../../../../hooks/useEmployeeProfile";
import { dateConversion } from "../../../../../utils/dateUtils";
import { canShowEditInfo, hasAccessPrivateFields } from "../../../utils/access";
import { formatAddress } from "../../../utils/format";

const norm = (v?: string | null) => (v ?? "").trim().toLowerCase();

/* ------------------- Personal (unchanged structure, just tidy) ------------------- */

const FIELD_ORDER_PERSONAL: Array<keyof PersonalInformation | string> = [
  "gender",
  "dob",
  "contactNumber",
  "alternateContact",
  "currentAddress",
  "permanentAddress",
];

const LABEL_MAP_PERSONAL: Partial<
  Record<keyof PersonalInformation | string, string>
> = {
  dob: "Date of Birth",
  contactNumber: "Contact No.",
  currentPincode: "PIN Code",
};

const BASE_OMIT_PERSONAL: Array<keyof PersonalInformation | string> = [
  "status",
  "callingSystem",
  "userId",
  "userType",
  "aadharCard",
  "aadharCardNumber",
  "panCard",
  "panCardNumber",
  "currentStreet",
  "currentCity",
  "currentState",
  "currentPincode",
  "permanentStreet",
  "permanentCity",
  "permanentState",
  "permanentPincode",
];

/* ------------------------------ Family (new) ------------------------------ */

const BASE_OMIT_FAMILY: Array<keyof FamilyInformation | string> = ["userId"];

const FIELD_ORDER_FAMILY_BASE: Array<keyof FamilyInformation | string> = [
  "fatherName",
  "fatherOccupation",
  "fatherContact",
  "alternateContact",
  "motherName",
  "motherOccupation",
  "motherContact",
  "maritalStatus",
];

const MARRIED_EXTRA_FIELDS: Array<keyof FamilyInformation | string> = [
  "anniversaryDate",
  "spouseName",
  "spouseOccupation",
  "spouseContact",
];

const EmployeeAboutInfoTab = () => {
  const dispatch = useAppDispatch();
  const { employeeProfileDetails, loggedInEmployee } = useEmployeeProfile();

  const personal = employeeProfileDetails?.employeePersonalInformation ?? null;
  const family = employeeProfileDetails?.employeeFamilyInformation ?? null;

  const employeeId = employeeProfileDetails?.jobInformation?.userId;
  const viewerId = loggedInEmployee?.id;
  const viewerRole = norm(loggedInEmployee?.user_type);

  const canEdit = useMemo(
    () =>
      !!employeeProfileDetails &&
      !!loggedInEmployee &&
      canShowEditInfo(employeeProfileDetails, loggedInEmployee),
    [employeeProfileDetails, loggedInEmployee]
  );

  const hasAccess = useMemo(() => {
    if (employeeId == null || viewerId == null || !loggedInEmployee?.user_type)
      return false;
    return hasAccessPrivateFields(
      Number(employeeId),
      Number(viewerId),
      loggedInEmployee.user_type
    );
  }, [employeeId, viewerId, loggedInEmployee]);

  // Personal dynamic omit (HR always redacts PAN/Aadhaar; others only if no access)
  const personalOmitKeys = useMemo(() => {
    if (viewerRole === "hr") return BASE_OMIT_PERSONAL;
    return BASE_OMIT_PERSONAL;
  }, [viewerRole, hasAccess]);

  const personalFormatters = useMemo(
    () =>
      hasAccess
        ? ({
            dob: (v: any) => dateConversion(v, false, false,false,),
            active: (v: any) =>
              v ? (
                <span className="text-success">Active</span>
              ) : (
                <span className="text-danger">Inactive</span>
              ),
            currentAddress: (_v: unknown, data: unknown) =>
              formatAddress(data as PersonalInformation, "current"),
            permanentAddress: (_v: unknown, data: unknown) =>
              formatAddress(data as PersonalInformation, "permanent"),
          } as const)
        : ({} as const),
    [hasAccess]
  );

  // Family field order & omit keys (memoized)
  const familyFieldOrder = useMemo(() => {
    if (!family) return FIELD_ORDER_FAMILY_BASE;
    return family.maritalStatus === "married"
      ? [...FIELD_ORDER_FAMILY_BASE, ...MARRIED_EXTRA_FIELDS]
      : FIELD_ORDER_FAMILY_BASE;
  }, [family]);

  const familyOmitKeys = useMemo(() => {
    if (!family) return BASE_OMIT_FAMILY;
    return family.maritalStatus !== "married"
      ? [...BASE_OMIT_FAMILY, ...MARRIED_EXTRA_FIELDS]
      : BASE_OMIT_FAMILY;
  }, [family]);


  const familyFormatters = useMemo(
    () =>
      ({
        anniversaryDate: (v: any) => dateConversion(v, false, false,false),
      } as const),
    []
  );

  const handleOpenModal = useCallback(
    (modalType: ModalType) => dispatch(openModal({ modalType })),
    [dispatch]
  );

  const handlePersonalInformationCard = useCallback(
    () => handleOpenModal(personalInformationModal),
    [handleOpenModal]
  );

  const handleFamilyInformationCard = useCallback(
    () => handleOpenModal(familyInformationModal),
    [handleOpenModal]
  );

  if (!personal && !family) return <Row />;

  return (
    <Row>
      <Col md="6">
        {personal && (
          <EmployeeInfoCard<PersonalInformation>
            title="Personal Information"
            entity={personal}
            showEditInfo={canEdit}
            canEdit={canEdit}
            fieldOrder={FIELD_ORDER_PERSONAL}
            labelMap={LABEL_MAP_PERSONAL}
            formatters={personalFormatters as any}
            omitKeys={personalOmitKeys}
            bodyClassName="p-3"
            iconOnClick={handlePersonalInformationCard}
          />
        )}
      </Col>

      <Col md="6">
        {family && (
          <EmployeeInfoCard<FamilyInformation>
            title="Family Information"
            entity={family}
            showEditInfo={canEdit}
            canEdit={canEdit}
            fieldOrder={familyFieldOrder}
            bodyClassName="p-3"
            omitKeys={familyOmitKeys}
            formatters={familyFormatters as any}
            iconOnClick={handleFamilyInformationCard}
          />
        )}
      </Col>
    </Row>
  );
};

export default EmployeeAboutInfoTab;
